import type { Dispatch, SetStateAction } from "react";
import OrganizerDatePicker, {
  formatDateInputValue,
  parseDateInputValue,
} from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput, {
  parseClockTimeToDate,
} from "../../../shared/components/OrganizerTimeInput";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  combineDateAndTime,
  extractDatePart,
  extractTimePart,
  parseHourMinuteToDuration,
} from "../../../shared/utils/dateDrafts";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";

function formatTimeInputValue(value?: Date | null) {
  if (!value) return "";
  return `${String(value.getHours()).padStart(2, "0")}:${String(
    value.getMinutes(),
  ).padStart(2, "0")}`;
}

type AdminChallengeScheduleFieldsProps = {
  challengeDetail: ChallengeDetail;
  challengeDurationDraft: string;
  challengeScheduledAtDraft: Date | null;
  challengeScheduledEndAtDraft: Date | null;
  isTournamentChallenge: boolean;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeDurationDraft: Dispatch<SetStateAction<string>>;
  setChallengeScheduledAtDraft: Dispatch<SetStateAction<Date | null>>;
  setChallengeScheduledEndAtDraft: Dispatch<SetStateAction<Date | null>>;
};

export default function AdminChallengeScheduleFields({
  challengeDurationDraft,
  challengeScheduledAtDraft,
  challengeScheduledEndAtDraft,
  isTournamentChallenge,
  setChallengeDetail,
  setChallengeDurationDraft,
  setChallengeScheduledAtDraft,
  setChallengeScheduledEndAtDraft,
}: AdminChallengeScheduleFieldsProps) {
  const t = siteTexts.admin;
  const createTexts = t.createChallengePage;

  return (
    <>
      <div className="field admin-form-block-start-date">
        <span>
          {isTournamentChallenge
            ? createTexts.tournamentStart
            : t.labels.scheduledAt}
        </span>
        {isTournamentChallenge ? (
          <OrganizerDatePicker
            label={createTexts.tournamentStart}
            value={
              challengeScheduledAtDraft
                ? formatDateInputValue(challengeScheduledAtDraft)
                : ""
            }
            disabledAfter={
              challengeScheduledEndAtDraft
                ? formatDateInputValue(challengeScheduledEndAtDraft)
                : undefined
            }
            onChange={(value) => {
              const nextDate = parseDateInputValue(value);
              if (!nextDate) {
                setChallengeScheduledAtDraft(null);
                setChallengeDetail((prev) =>
                  prev ? { ...prev, scheduledAt: null } : prev,
                );
                return;
              }
              const normalized = extractDatePart(nextDate);
              setChallengeScheduledAtDraft(normalized);
              setChallengeDetail((prev) =>
                prev ? { ...prev, scheduledAt: normalized.toISOString() } : prev,
              );
            }}
          />
        ) : (
          <div className="admin-row-two">
            <OrganizerDatePicker
              label={t.labels.date}
              value={
                challengeScheduledAtDraft
                  ? formatDateInputValue(challengeScheduledAtDraft)
                  : ""
              }
              onChange={(value) => {
                const nextDate = parseDateInputValue(value);
                if (!nextDate) {
                  setChallengeScheduledAtDraft(null);
                  setChallengeDetail((prev) =>
                    prev ? { ...prev, scheduledAt: null } : prev,
                  );
                  return;
                }
                const next = combineDateAndTime(
                  nextDate,
                  challengeScheduledAtDraft
                    ? extractTimePart(challengeScheduledAtDraft)
                    : null,
                );
                setChallengeScheduledAtDraft(next);
                setChallengeDetail((prev) =>
                  prev ? { ...prev, scheduledAt: next.toISOString() } : prev,
                );
              }}
            />
            <OrganizerTimeInput
              label={t.labels.time}
              value={formatTimeInputValue(challengeScheduledAtDraft)}
              onChange={(value) => {
                if (!challengeScheduledAtDraft) return;
                const nextTime = parseClockTimeToDate(value);
                if (!nextTime) return;
                const date = extractDatePart(challengeScheduledAtDraft);
                const next = combineDateAndTime(date, nextTime);
                setChallengeScheduledAtDraft(next);
                setChallengeDetail((prev) =>
                  prev ? { ...prev, scheduledAt: next.toISOString() } : prev,
                );
              }}
              disabled={!challengeScheduledAtDraft}
            />
          </div>
        )}
      </div>

      {isTournamentChallenge && (
        <div className="field admin-form-block-end-date">
          <span>{createTexts.tournamentEnd}</span>
          <OrganizerDatePicker
            label={createTexts.tournamentEnd}
            value={
              challengeScheduledEndAtDraft
                ? formatDateInputValue(challengeScheduledEndAtDraft)
                : ""
            }
            disabledBefore={
              challengeScheduledAtDraft
                ? formatDateInputValue(challengeScheduledAtDraft)
                : undefined
            }
            onChange={(value) => {
              const nextDate = parseDateInputValue(value);
              if (!nextDate) {
                setChallengeScheduledEndAtDraft(null);
                setChallengeDetail((prev) =>
                  prev ? { ...prev, scheduledEndAt: null } : prev,
                );
                return;
              }
              const normalized = extractDatePart(nextDate);
              setChallengeScheduledEndAtDraft(normalized);
              setChallengeDetail((prev) =>
                prev
                  ? { ...prev, scheduledEndAt: normalized.toISOString() }
                  : prev,
              );
            }}
          />
        </div>
      )}

      {!isTournamentChallenge && (
        <label className="field">
          <span>{t.labels.durationSeconds}</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={challengeDurationDraft}
            onChange={(event) => {
              const raw = event.target.value;
              setChallengeDurationDraft(raw);
              const trimmed = raw.trim();
              if (!trimmed) {
                setChallengeDetail((prev) =>
                  prev ? { ...prev, durationSeconds: 0 } : prev,
                );
                return;
              }
              const parsed = parseHourMinuteToDuration(raw);
              if (parsed === null) return;
              setChallengeDetail((prev) =>
                prev ? { ...prev, durationSeconds: parsed } : prev,
              );
            }}
          />
        </label>
      )}
    </>
  );
}
